// Distribución de conversaciones por carpeta. Sin recharts a propósito: filas
// nombre + barra de progreso + count se leen más rápido que un chart para una
// lista corta, y el nombre nunca se trunca contra un eje.

import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../../utils/cn";
import { Card } from "../../../../components/ui/Card";
import { formatInt } from "./format";

/**
 * @param {object} props
 * @param {Array<{folder_id: string|null, name: string|null, count: number}>} props.rows
 *   - por_carpeta del backend. folder_id null = conversaciones sin carpeta;
 *     name null con folder_id = la carpeta fue borrada después del período.
 * @param {string} [props.className]
 */
function FolderDistribution({ rows, className }) {
  const { t } = useTranslation();

  const data = useMemo(
    () => [...(rows || [])].sort((a, b) => (b.count || 0) - (a.count || 0)),
    [rows],
  );

  const max = data.length > 0 ? Math.max(1, data[0].count || 0) : 1;
  const hasData = data.length > 0;

  return (
    <Card padding="md" className={className}>
      <h2 className="text-sm font-semibold text-fg mb-3">{t("Distribución por carpeta")}</h2>

      {hasData ? (
        <ul className="space-y-3">
          {data.map((row) => {
            // Sin folder_id = sin carpeta; con folder_id pero sin nombre = la
            // carpeta se eliminó (el DELETE desasigna pero el agregado del
            // período la sigue contando). Ambos van atenuados: no son carpetas
            // operables hoy.
            const isNone = !row.folder_id;
            const isDeleted = !!row.folder_id && !row.name;
            const label = isNone
              ? t("Sin carpeta")
              : isDeleted
                ? t("Carpeta eliminada")
                : row.name;
            const pct = Math.round(((row.count || 0) / max) * 100);
            return (
              <li key={row.folder_id || "none"} className="flex items-center gap-3">
                <span
                  className={cn(
                    "w-32 sm:w-40 shrink-0 text-xs truncate",
                    isNone || isDeleted ? "text-fg-muted" : "text-fg",
                  )}
                  title={label}
                >
                  {label}
                </span>
                <span className="flex-1 h-1.5 rounded-full bg-bg-muted overflow-hidden">
                  <span
                    className="block h-1.5 rounded-full bg-brand-500"
                    style={{ width: `${pct}%` }}
                    aria-hidden="true"
                  />
                </span>
                <span className="w-12 shrink-0 text-right text-xs text-fg tabular-nums">
                  {formatInt(row.count)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="h-[120px] flex items-center justify-center text-[12px] md:text-[11px] text-fg-muted">
          {t("Sin conversaciones en el período.")}
        </div>
      )}
    </Card>
  );
}

export default memo(FolderDistribution);
